import * as React from 'react';
import { toast } from 'react-toastify';
import { useCallbackObservable } from 'react-use-observable';

import { CloseOutlined } from '@ant-design/icons';
import { Button, Checkbox, Col, Modal, Row, Spin } from 'antd';

import CoProducerClass from 'classes/CoProducer';
import CustomMaskedInput from 'components/MaskedInput';
import { finalize } from 'rxjs/operators';
import { sendEventToGA } from 'v2/helpers/analytics';
import productService from 'v2/services/product';

type SolidaritySquadModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  productId: number;
};

const PROJECT_ELEVAR_ID = 75879698;
const DEFAULT_COMMISSION_VALUE = 5;
const GA_CATEGORY = 'SS Modal - Projeto Elevar';

enum CloseReason {
  NOT_NOW_BUTTON,
  CLOSE_BUTTON
}

function SolidaritySquadModal({ isOpen, handleClose: handleCloseProp, productId }: SolidaritySquadModalProps) {
  const [updateCoproducers] = useCallbackObservable((productId, coproducers) => {
    setIsAdding(true);
    return productService.updateCoproducers(productId, coproducers).pipe(
      finalize(() => {
        setIsAdding(false);
        toast.success('Doação automática configurada com sucesso');
        handleClose();
      })
    );
  }, []);

  const [commissionValue, setCommissionValue] = React.useState(DEFAULT_COMMISSION_VALUE);
  const [commissionError, setCommissionError] = React.useState('');
  const [isTermsAccepted, setIsTermsAccepted] = React.useState(false);
  const [isAdding, setIsAdding] = React.useState(false);

  function validateCommissionValue(value: number) {
    if (value < 0.01) setCommissionError('O valor de porcentagem tem que ser no mínimo 0,01');
    else setCommissionError(null);
  }

  function handleCommissionValueChange(value: string) {
    const formattedValue = Number(value.replace(',', '.'));
    validateCommissionValue(formattedValue);
    setCommissionValue(formattedValue);
  }

  function handleTermsChange(e: any) {
    setIsTermsAccepted(e.target.checked);
  }

  function createElevarAsCoProducer() {
    return new CoProducerClass({
      new: true,
      pending: false,
      business_name: 'Instituto Elevar',
      email: 'contato@projetoelevar.com.br',
      comission_type: 'P',
      comission_value: commissionValue,
      customer_id: PROJECT_ELEVAR_ID
    });
  }

  function removeCommissionFromProducer(coproducers: any[]) {
    return coproducers.map(coproducer => {
      if (coproducer.is_producer) coproducer.comission_value -= commissionValue;
      return coproducer;
    });
  }

  function handleSubmit(productId: number) {
    productService.getCoproducers(productId).subscribe(coproducers => {
      const updatedCoproducers = removeCommissionFromProducer(coproducers.active);
      updateCoproducers(productId, [...updatedCoproducers, createElevarAsCoProducer()]);
    });
    sendEventToGA({ category: GA_CATEGORY, action: 'Save Donation' });
  }

  function handleClose(closeReason?: CloseReason) {
    handleCloseProp();
    if (closeReason === CloseReason.CLOSE_BUTTON) sendEventToGA({ category: GA_CATEGORY, action: 'Close' });
    if (closeReason === CloseReason.NOT_NOW_BUTTON) sendEventToGA({ category: GA_CATEGORY, action: 'Not Now' });
  }

  return (
    <Modal
      open={isOpen}
      onCancel={() => handleClose()}
      width={1000}
      styles={{ content: { padding: '0', borderRadius: '4px' } }}
      closeIcon={<CloseOutlined style={{ fontSize: 16 }} />}
      footer={null}
    >
      <div className='flex gap-4'>
        <div className='w-[40%] overflow-hidden rounded-l max-md:hidden'>
          <img className='h-full w-full object-cover object-center' src='/img/modal-elevar.png' alt='Projeto elevar' />
        </div>
        <Row className='w-[60%] p-3' gutter={[16, 16]}>
          <Col span={24}>
            <p className='text-2xl font-bold'>Faça a diferença e transforme vidas!</p>
            <p className='text-base font-bold'>Junte-se ao Instituto Elevar, uma organização de Assistência Social</p>
            <p className='border-border mt-2 border-t pt-[10px] text-base'>
              Transformamos vidas por meio da educação, tecnologia, esporte, arte, cultura, qualificação profissional e
              empreendedorismo.
            </p>
          </Col>
          <Col span={24}>
            <CustomMaskedInput
              id='comission_value'
              className='[&>label]:font-bold'
              name='comission_value'
              mask='percent'
              label='Porcentagem da contribuição'
              helperText={commissionError}
              error={!!commissionError}
              startAdornment='%'
              fullWidth
              disableUnderline
              onChange={handleCommissionValueChange}
              value={String(commissionValue)}
              InputLabelProps={{ shrink: true }}
              InputProps={{ min: 0.01 }}
            />
          </Col>
          <Col span={24}>
            <label className='flex items-center gap-2'>
              <Checkbox checked={isTermsAccepted} onChange={handleTermsChange} />
              {`Concordo em contribuir com ${commissionValue}% sobre cada venda deste produto para o Instituto Elevar.`}
            </label>
          </Col>
          <Col span={24}>
            <div className='flex gap-3 max-md:flex-col'>
              <Button onClick={() => handleClose(CloseReason.NOT_NOW_BUTTON)}>Não quero contribuir</Button>
              <Button
                type='primary'
                disabled={!!commissionError || !isTermsAccepted || isAdding}
                icon={isAdding ? <Spin size='small' /> : undefined}
                onClick={() => handleSubmit(productId)}
              >
                Fazer a diferença
              </Button>
            </div>
          </Col>
        </Row>
      </div>
    </Modal>
  );
}

export { SolidaritySquadModal };
